import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  Paper,
  Chip,
  Box,
} from "@mui/material";
import { formatDate } from "../utils/formatDate";
import PropTypes from "prop-types";

const TransactionTable = ({ transactions }) => {
  const [sortField, setSortField] = useState("date_posted");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Extract unique categories
  const uniqueCategories = useMemo(
    () => Array.from(new Set(transactions.map((t) => t.credit_category))).filter(Boolean),
    [transactions]
  );

  // Reset pagination when filters or search change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedCategories]);

  // Handle sorting
  const handleSort = (field) => {
    setSortDirection((prev) => (sortField === field && prev === "asc" ? "desc" : "asc"));
    setSortField(field);
  };

  // Handle category filtering
  const handleCategoryClick = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  // Filter and sort transactions
  const filteredAndSortedTransactions = useMemo(() => {
    const filtered = transactions.filter(
      (transaction) =>
        (selectedCategories.length === 0 || selectedCategories.includes(transaction.credit_category)) &&
        (transaction.original_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transaction.amount.toString().includes(searchTerm))
    );

    return filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [transactions, searchTerm, sortField, sortDirection, selectedCategories]);

  // Pagination
  const paginatedTransactions = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredAndSortedTransactions.slice(start, end);
  }, [filteredAndSortedTransactions, page, rowsPerPage]);

  return (
    <Box>
      {/* Search Input */}
      <TextField
        variant="outlined"
        label="Search transactions"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Category Chips */}
      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {uniqueCategories.map((category) => (
          <Chip
            key={category}
            label={category}
            clickable
            onClick={() => handleCategoryClick(category)}
            sx={{
              backgroundColor: selectedCategories.includes(category) ? "#1976d2" : "#f0f0f0",
              color: selectedCategories.includes(category) ? "#fff" : "#000",
              fontWeight: selectedCategories.includes(category) ? "bold" : "normal",
            }}
          />
        ))}
      </Box>

      {/* Transaction Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === "date_posted"}
                  direction={sortField === "date_posted" ? sortDirection : "asc"}
                  onClick={() => handleSort("date_posted")}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "original_description"}
                  direction={sortField === "original_description" ? sortDirection : "asc"}
                  onClick={() => handleSort("original_description")}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "credit_category"}
                  direction={sortField === "credit_category" ? sortDirection : "asc"}
                  onClick={() => handleSort("credit_category")}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === "amount"}
                  direction={sortField === "amount" ? sortDirection : "asc"}
                  onClick={() => handleSort("amount")}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((transaction, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {transaction.date_posted
                      ? formatDate(transaction.date_posted, "short")
                      : "N/A"}
                  </TableCell>
                  <TableCell>{transaction.original_description || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.credit_category || "N/A"}
                      clickable
                      onClick={() => handleCategoryClick(transaction.credit_category)}
                      sx={{
                        backgroundColor: selectedCategories.includes(transaction.credit_category)
                          ? "#1976d2"
                          : "#f5f5f5",
                        color: selectedCategories.includes(transaction.credit_category)
                          ? "#fff"
                          : "#000",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: transaction.amount < 0 ? "green" : "red",
                      fontWeight: "bold",
                    }}
                  >
                    ${transaction.amount.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No transactions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredAndSortedTransactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
      />
    </Box>
  );
};

TransactionTable.propTypes = {
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      date_posted: PropTypes.string,
      original_description: PropTypes.string,
      credit_category: PropTypes.string,
      amount: PropTypes.number,
    })
  ).isRequired,
};

export default TransactionTable;
